<template>
  <article
    :class="fileItemClasses"
    :aria-label="!props.isClickable ? props.ariaLabel : undefined"
    data-cy="ds-file-item"
  >
    <component
      :is="clickTargetElement"
      v-if="props.isClickable"
      :class="DS_FILE_ITEM_CLASSES.ClickTarget"
      :href="clickTargetAttributes.href"
      :target="clickTargetAttributes.target"
      :type="clickTargetAttributes.type"
      :disabled="clickTargetAttributes.disabled"
      :role="clickTargetAttributes.role"
      :aria-disabled="props.disabled || undefined"
      :aria-label="props.ariaLabel ?? displayFileName"
      :tabindex="props.disabled ? -1 : undefined"
      data-cy="ds-file-item__click-target"
      @click="handleClick"
    />

    <div
      v-if="props.thumbnail"
      :class="DS_FILE_ITEM_CLASSES.Thumbnail"
      data-cy="ds-file-item__thumbnail"
    >
      <DSThumbnail
        :content="thumbnailContent"
        :file-name="props.fileName"
        :src="props.thumbnailSrc"
        :overlay-icon="props.isClickable && !props.disabled ? props.thumbnailOverlayIcon : undefined"
        :loading="props.showProgressBar && !hasErrors"
        size="xs"
      />
    </div>

    <DSFileItemContent
      :file-name="props.fileName"
      :file-version="props.fileVersion"
      :file-uploaded-date="props.fileUploadedDate"
      :file-type="props.fileType"
      :file-size="props.fileSize"
      :file-description="props.fileDescription"
      :author="props.showAuthor ? props.author : undefined"
      :error-messages="props.errorMessages"
    />

    <div
      v-if="hasActionsSlot && !props.disabled"
      :class="DS_FILE_ITEM_CLASSES.Actions"
      data-cy="ds-file-item__actions"
      @click.stop
    >
      <slot name="actions" />
    </div>

    <DSProgressBar
      v-if="props.showProgressBar"
      :class="DS_FILE_ITEM_CLASSES.Progress"
      :value="props.progress"
      :max="props.progressMax"
      :variant="hasErrors ? 'danger' : 'primary'"
      data-cy="ds-file-item__progress"
    />
  </article>
</template>

<script setup lang="ts">
import { computed, type VNode } from "vue";
import DSProgressBar from "@/src/design_system/feedback/progress_bar/components/DSProgressBar.vue";
import { DS_PROGRESS_COMPLETE } from "@/src/design_system/feedback/progress_bar/constants/progress";
import DSFileItemContent from "@/src/design_system/data_display/file_item/components/DSFileItemContent.vue";
import DSThumbnail from "@/src/design_system/data_display/thumbnail/components/DSThumbnail.vue";
import { DS_THUMBNAIL_CONTENT, type DSThumbnailContent } from "@/src/design_system/data_display/thumbnail/types";
import { DS_FILE_ITEM_CLASSES } from "@/src/design_system/data_display/file_item/constants";
import type { DSFileItemEmits, DSFileItemProps } from "@/src/design_system/data_display/file_item/types";
import { formatFileNameWithVersion } from "@/src/design_system/data_display/file_item/utils/formatFileNameWithVersion";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { isPreviewableImage } from "@/src/design_system/shared/utils/fileName";
import { hasSlotContent } from "@/src/shared/utils/slots";

const props = withDefaults(defineProps<DSFileItemProps>(), {
  fileVersion: undefined,
  fileUploadedDate: undefined,
  fileType: undefined,
  fileSize: undefined,
  fileDescription: undefined,
  thumbnail: undefined,
  thumbnailSrc: undefined,
  thumbnailOverlayIcon: undefined,
  author: undefined,
  showAuthor: true,
  errorMessages: () => [],
  showProgressBar: false,
  progress: 0,
  progressMax: DS_PROGRESS_COMPLETE,
  disabled: false,
  isClickable: false,
  href: undefined,
  target: undefined,
  ariaLabel: undefined,
});

const emit = defineEmits<DSFileItemEmits>();
const slots = defineSlots<{
  actions?: () => VNode[];
}>();

const hasActionsSlot = computed(() => hasSlotContent(slots, "actions"));
const hasErrors = computed(() => props.errorMessages.length > 0);
const displayFileName = computed(() => formatFileNameWithVersion(props.fileName, props.fileVersion));
const thumbnailContent = computed<DSThumbnailContent>(() =>
  props.thumbnailSrc && isPreviewableImage(props.fileName) ? DS_THUMBNAIL_CONTENT.Image : DS_THUMBNAIL_CONTENT.Icon
);
const clickTargetElement = computed(() => (props.href ? "a" : "button"));
const clickTargetAttributes = computed(() => {
  const attributes: {
    href?: string;
    target?: string;
    type?: "button";
    disabled?: boolean;
    role?: "link";
  } = {};

  if (props.href) {
    attributes.target = props.target;
    if (props.disabled) attributes.role = "link";
    else attributes.href = props.href;
  } else {
    attributes.type = "button";
    if (props.disabled) attributes.disabled = true;
  }

  return attributes;
});
const fileItemClasses = computed(() => ({
  [DS_FILE_ITEM_CLASSES.Root]: true,
  [DS_FILE_ITEM_CLASSES.Clickable]: props.isClickable,
  [DS_STATE_CLASSES.Error]: hasErrors.value,
  [DS_STATE_CLASSES.Disabled]: props.disabled,
}));

function handleClick(event: MouseEvent): void {
  if (props.disabled) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  emit("click", event);
}
</script>
